import { useCallback, useState } from 'react'

function useFetchHandler() {
    const [requestState, setRequestState] = useState({
        loading: false,
        error: null,
        success: false,
    });

    /**
     * Handles HTTP requests.
     *
     * @async
     * @function
     * @param {string} method - The HTTP method for the request (e.g., 'GET', 'POST').
     * @param {string} route - The URL or endpoint to send the request to.
     * @param {Object} [body] - The body of the request, for methods like POST and PUT.
     * @param {boolean} [withAuthToken=false] - Whether to include an auth token in the headers.
     * @param {Function} [dataHandler] - Callback to handle the response data.
     * @returns {Promise<void>} - Resolves when the request completes.
     */
    const requestHandler = useCallback(async (
        method,
        route,
        body = null,
        withAuthToken = false,
        dataHandler,
        autoSetHeaders = false,
        throwError = false
    ) => {
        setRequestState({ loading: true, error: null, success: false });

        try {
            // Set up headers
            const headers = autoSetHeaders ? {} : {
                'Content-Type': 'application/json',
            };

            if (withAuthToken) {
                const token = localStorage.getItem('access_token');
                if (token) headers['Authorization'] = `Bearer ${token}`;
            }

            // Set up request options
            const options = {
                method,
                headers,
                body: body,
            };

            // Make the request
            const response = await fetch(process.env.REACT_APP_GRO_BACKEND_URI + `/${route}`, options);

            // Handle response
            if (!response.ok) {
                throw new Error(`Error ${response.status}: ${response.statusText}`);
            }

            const data = await response.json();

            if (!data?.success) {
                throw new Error(`Error: ${data?.message}`);
            }

            if (dataHandler) {
                dataHandler(data);
            }

            setRequestState(prev => ({
                success: true,
                error: null,
                loading: false,
            }))
        } catch (error) {
            setRequestState({ loading: false, success: false, error: error?.message });
            if (throwError) throw new Error(error);
        } finally {
            setRequestState((prevState) => ({ ...prevState, loading: false }));
        }
    }, []);

    const resetRequestState = useCallback(() => {
        setRequestState({
            loading: false,
            error: null,
            success: false,
        });
    }, [])

    return {
        loading: requestState.loading,
        error: requestState.error,
        handleFetch: requestHandler,
        responseSuccess: requestState.success,
        resetRequestState
    };
}

export default useFetchHandler;
